import React, { useEffect, useState } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Globe, Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import PageHero from "./PageHero";
import Seo from "../components/common/Seo";
import LeadCallout from "../components/common/LeadCallout";
import { Reveal, SectionEyebrow, ArrowIcon } from "../components/common/Section";
import { useI18n } from "../lib/i18n";
import { resolveImage } from "../components/admin/ImageUpload";
import api from "../lib/api";
import BitrixContactForm from "../components/home/BitrixContactForm";

export const CompaniesListPage = () => {
  const { pick, lang } = useI18n();
  const { settings } = useOutletContext() || {};
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api
      .get("/public/group-companies")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, []);

  const title = { tr: "Şirketlerimiz & Faaliyet Alanlarımız", en: "Our Companies & Businesses", ar: "شركاتنا وقطاعاتنا" };
  const sub = {
    tr: "Fırat Holding çatısı altındaki çok sektörlü şirketlerimiz ve faaliyet alanlarımızı keşfedin.",
    en: "Discover our diversified companies and business sectors under Fırat Holding.",
    ar: "اكتشف شركاتنا متعددة القطاعات وقطاعات أعمالنا تحت مظلة مجموعة فرات القابضة.",
  };

  return (
    <div data-testid="companies-list-page">
      <Seo fallbackTitle={pick(title)} fallbackDescription={pick(sub)} />
      <PageHero eyebrow={lang === "tr" ? "Holding" : lang === "ar" ? "المجموعة" : "Holding"} title={title} subtitle={sub} image="https://images.unsplash.com/photo-1481026469463-66327c86e544" />
      <section className="bg-white">
        <div className="container-x py-16 md:py-24 grid grid-cols-2 gap-4 md:gap-6">
          {!loaded
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} data-testid={`companies-tile-loading-${i}`}>
                  <div className="aspect-[16/10] bg-black/5 animate-pulse" />
                  <div className="mt-4 h-3 w-40 bg-black/5 animate-pulse" />
                  <div className="mt-2 h-5 w-2/3 bg-black/5 animate-pulse" />
                </div>
              ))
            : rows.map((c, i) => {
            const External = !!c.website_url;
            const body = (
              <div className="zoom-parent block group">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={resolveImage(c.cover_image)} alt={pick(c.name)} className="w-full h-full object-cover zoom-img" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 to-transparent" />
                  <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                    <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-primary-bright">{pick(c.sector)}</div>
                    <div className="font-serif text-3xl md:text-4xl mt-3">{pick(c.name)}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-brand-medium max-w-md">{pick(c.description)}</p>
                    {c.established ? <div className="mt-2 text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">Est. {c.established}</div> : null}
                  </div>
                  <div className="link-gold shrink-0 mt-2">
                    {External ? <>Visit <ExternalLink size={12} /></> : <>Discover <ArrowIcon /></>}
                  </div>
                </div>
              </div>
            );
            return (
              <motion.div
                key={c.id || c.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                data-testid={`companies-tile-${c.slug}`}
              >
                {External ? <a href={c.website_url} target="_blank" rel="noreferrer">{body}</a> : <Link to={`/companies/${c.slug}`}>{body}</Link>}
              </motion.div>
            );
          })}
        </div>
      </section>
      <LeadCallout settings={settings} />
    </div>
  );
};

/* -----------------------------------------------------------
 * Company Detail Page — editorial layout with logo header,
 * company-specific projects, and per-company Bitrix contact form
 * ----------------------------------------------------------- */
export const CompanyDetailPage = () => {
  const { slug } = useParams();
  const { pick, lang } = useI18n();
  const { settings } = useOutletContext() || {};
  const [c, setC] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get(`/public/group-companies/${slug}`)
      .then((r) => { if (mounted) setC(r.data); })
      .catch(() => { if (mounted) setC(null); });
    api.get(`/public/projects`, { params: { group_company: slug, limit: 6 } })
      .then((r) => { if (mounted) setProjects(r.data || []); })
      .catch(() => { if (mounted) setProjects([]); });
    return () => { mounted = false; };
  }, [slug]);

  if (!c) {
    return (
      <div className="bg-white" data-testid="company-detail-loading">
        {/* Hero placeholder */}
        <section className="relative h-[60vh] min-h-[420px] bg-brand-light">
          <div className="container-x h-full flex flex-col justify-end pb-16 gap-4">
            <div className="h-3 w-32 bg-black/10 animate-pulse rounded-sm" />
            <div className="h-12 w-2/3 bg-black/10 animate-pulse rounded-sm" />
            <div className="h-4 w-1/2 bg-black/10 animate-pulse rounded-sm" />
          </div>
        </section>
        <section className="container-x py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 space-y-4">
            <div className="h-3 w-24 bg-black/5 animate-pulse rounded-sm" />
            <div className="h-8 w-3/4 bg-black/5 animate-pulse rounded-sm" />
            <div className="h-4 w-full bg-black/5 animate-pulse rounded-sm" />
            <div className="h-4 w-5/6 bg-black/5 animate-pulse rounded-sm" />
            <div className="h-4 w-4/6 bg-black/5 animate-pulse rounded-sm" />
          </div>
          <div className="lg:col-span-4">
            <div className="bg-brand-light p-8 h-64 animate-pulse" />
          </div>
        </section>
      </div>
    );
  }

  const services = c.services || [];
  const gallery = c.gallery || [];
  const hasMap = c.map_lat != null && c.map_lng != null;
  const mapBbox = hasMap ? `${c.map_lng - 0.03}%2C${c.map_lat - 0.02}%2C${c.map_lng + 0.03}%2C${c.map_lat + 0.02}` : "";
  const mapMarker = hasMap ? `${c.map_lat}%2C${c.map_lng}` : "";

  // Resolve company-specific Bitrix form
  const allForms = settings?.bitrix_forms || [];
  const matchedForm = c.bitrix_form_key
    ? allForms.find((f) => f.key === c.bitrix_form_key)
    : null;
  const fallbackForm = allForms.find((f) => f.key === "iletisim") || allForms.find((f) => f.is_default) || allForms[0];
  const activeForm = matchedForm || fallbackForm;
  const formsForBitrix = activeForm ? [{ ...activeForm, is_default: true }] : [];

  return (
    <div data-testid="company-detail-page" className="bg-white">
      <Seo fallbackTitle={pick(c.name)} fallbackDescription={pick(c.description)} fallbackImage={c.cover_image} />

      <PageHero eyebrow={pick(c.sector)} title={c.name} subtitle={c.tagline} image={resolveImage(c.cover_image)} />

      {/* Logo header strip — sits under the hero, ties brand identity to the company */}
      {c.logo ? (
        <section className="bg-brand-navy text-white" data-testid="company-logo-strip">
          <div className="container-x py-8 md:py-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-shrink-0 bg-white/95 px-6 py-4 md:px-8 md:py-5 rounded-sm shadow-lg inline-flex items-center justify-center max-w-[280px]">
              <img
                src={resolveImage(c.logo)}
                alt={`${pick(c.name)} logo`}
                className="h-12 md:h-14 w-auto object-contain"
                data-testid="company-logo"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary-bright font-medium">
                {lang === "tr" ? "Fırat Holding Şirketi" : lang === "ar" ? "شركة تابعة لفرات القابضة" : "A Fırat Holding Company"}
              </div>
              <div className="font-serif text-2xl md:text-3xl mt-2 leading-tight">
                {pick(c.name)}
              </div>
              {pick(c.tagline) ? (
                <div className="mt-2 text-white/75 text-sm md:text-base max-w-2xl leading-relaxed">
                  {pick(c.tagline)}
                </div>
              ) : null}
            </div>
            {c.website_url ? (
              <a
                href={c.website_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-white border border-white/20 hover:border-white/60 hover:bg-white/5 transition-all px-5 py-3 text-xs tracking-[0.24em] uppercase font-medium shrink-0"
                data-testid="company-website-cta"
              >
                <Globe size={14} strokeWidth={1.5} />
                <span>{lang === "tr" ? "Kurumsal Site" : lang === "ar" ? "الموقع" : "Visit Site"}</span>
                <ExternalLink size={12} strokeWidth={1.5} />
              </a>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Overview + at-a-glance sidebar */}
      <section className="bg-white">
        <div className="container-x py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8">
            <Reveal>
              <SectionEyebrow>{lang === "tr" ? "Hakkında" : lang === "ar" ? "عن الشركة" : "About"}</SectionEyebrow>
              <h2 className="display display-md mt-4">{pick(c.tagline)}</h2>
              <p className="mt-6 text-brand-dark text-lg leading-relaxed">{pick(c.description)}</p>
              {pick(c.long_description) ? (
                <p className="mt-4 text-brand-medium leading-relaxed">{pick(c.long_description)}</p>
              ) : null}
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="bg-brand-light p-6 md:p-8 border-s-2 border-brand-primary">
                <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
                  {lang === "tr" ? "Şirket Bilgileri" : lang === "ar" ? "معلومات الشركة" : "Company Info"}
                </div>
                <ul className="mt-6 space-y-4 text-sm">
                  {pick(c.sector) ? (
                    <li><div className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium">Sektör</div><div className="text-brand-navy mt-1 font-medium">{pick(c.sector)}</div></li>
                  ) : null}
                  {c.established ? (
                    <li><div className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium">Kuruluş</div><div className="text-brand-navy mt-1 font-medium">{c.established}</div></li>
                  ) : null}
                  {c.country ? (
                    <li><div className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium">Ülke</div><div className="text-brand-navy mt-1 font-medium">{c.country}</div></li>
                  ) : null}
                  {c.website_url ? (
                    <li className="pt-4 border-t border-black/10">
                      <a href={c.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-primary-bright hover:text-brand-navy font-medium" data-testid="company-website">
                        <Globe size={14} strokeWidth={1.5} />
                        <span className="text-xs uppercase tracking-[0.2em]">{lang === "tr" ? "Web Sitesi" : lang === "ar" ? "الموقع" : "Website"}</span>
                        <ExternalLink size={12} strokeWidth={1.5} />
                      </a>
                    </li>
                  ) : null}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services */}
      {services.length ? (
        <section className="bg-brand-light" data-testid="company-services">
          <div className="container-x py-16 md:py-24">
            <Reveal>
              <SectionEyebrow>{lang === "tr" ? "Hizmetlerimiz" : lang === "ar" ? "خدماتنا" : "Our Services"}</SectionEyebrow>
              <h2 className="display display-md mt-4">{lang === "tr" ? "Sunduğumuz Hizmetler" : lang === "ar" ? "الخدمات التي نقدمها" : "What We Offer"}</h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
              {services.map((sv, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="bg-white border border-black/5 hover:border-brand-primary p-5 md:p-6 transition-colors group"
                  data-testid={`service-${i}`}
                >
                  <div className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-primary-bright">0{i + 1}</div>
                  <div className="mt-3 font-serif text-lg md:text-xl text-brand-navy leading-tight">{pick(sv)}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Company projects — "Bu Şirketin Projeleri" */}
      {projects.length ? (
        <section className="bg-white" data-testid="company-projects">
          <div className="container-x py-16 md:py-24">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <Reveal>
                <SectionEyebrow>
                  {lang === "tr" ? "Portföy" : lang === "ar" ? "المحفظة" : "Portfolio"}
                </SectionEyebrow>
                <h2 className="display display-md mt-4">
                  {lang === "tr"
                    ? "Bu Şirketin Projeleri"
                    : lang === "ar"
                      ? "مشاريع هذه الشركة"
                      : "Projects by This Company"}
                </h2>
              </Reveal>
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-brand-primary hover:text-brand-navy font-medium"
                data-testid="company-projects-all"
              >
                {lang === "tr" ? "Tüm Projeler" : lang === "ar" ? "كل المشاريع" : "All Projects"}
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {projects.map((p, i) => (
                <motion.div
                  key={p.id || p.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  data-testid={`company-project-${p.slug}`}
                >
                  <Link to={`/projects/${p.slug}`} className="zoom-parent block group">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={resolveImage(p.cover_image)} alt={pick(p.name)} className="w-full h-full object-cover zoom-img" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                        <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary-bright">
                          {p.category}
                        </div>
                        <div className="font-serif text-xl md:text-2xl leading-tight mt-1">{pick(p.name)}</div>
                        {pick(p.location) ? (
                          <div className="text-xs text-white/75 mt-1">{pick(p.location)}</div>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Gallery */}
      {gallery.length ? (
        <section className="bg-brand-light" data-testid="company-gallery">
          <div className="container-x py-16 md:py-24">
            <Reveal>
              <SectionEyebrow>{lang === "tr" ? "Galeri" : lang === "ar" ? "معرض الصور" : "Gallery"}</SectionEyebrow>
              <h2 className="display display-md mt-4">{lang === "tr" ? "Görsel Galeri" : lang === "ar" ? "معرض" : "Visual Gallery"}</h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
              {gallery.map((g, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden zoom-parent group">
                  <img src={resolveImage(g)} alt="" className="w-full h-full object-cover zoom-img" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Bitrix contact form — company-specific */}
      {formsForBitrix.length ? (
        <section id="basvuru" className="bg-white" data-testid="company-bitrix">
          <div className="container-x py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-4">
              <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-3">
                {lang === "tr" ? "İletişim" : lang === "ar" ? "تواصل معنا" : "Get in Touch"}
              </div>
              <h2
                className="font-serif text-brand-navy leading-[1.05]"
                style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}
              >
                {pick(c.name)}
                {lang === "tr" ? " ile İletişime Geçin" : lang === "ar" ? "" : ""}
              </h2>
              <p className="mt-4 text-brand-medium leading-relaxed">
                {lang === "tr"
                  ? "Aşağıdaki formu doldurarak ekibimizle iletişime geçebilirsiniz. En kısa sürede size dönüş yapacağız."
                  : lang === "ar"
                    ? "املأ النموذج أدناه للتواصل مع فريقنا وسنعود إليك في أقرب وقت ممكن."
                    : "Fill out the form below to reach our team. We will get back to you shortly."}
              </p>
            </div>
            <div className="lg:col-span-8">
              <BitrixContactForm forms={formsForBitrix} />
            </div>
          </div>
        </section>
      ) : null}

      {/* Contact info + Map */}
      {(c.phone || c.email || pick(c.address) || hasMap) ? (
        <section className="bg-brand-navy text-white relative overflow-hidden" data-testid="company-contact">
          <div className="grain absolute inset-0 pointer-events-none" />
          <div className="container-x py-16 md:py-24 relative grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-primary-bright font-medium">
                  {lang === "tr" ? "İletişim & Konum" : lang === "ar" ? "التواصل والموقع" : "Contact & Location"}
                </div>
                <h2 className="display display-md mt-4 text-white">{pick(c.name)}</h2>
                <ul className="mt-8 space-y-5">
                  {pick(c.address) ? (
                    <li className="flex items-start gap-3">
                      <MapPin size={18} strokeWidth={1.5} className="text-brand-primary-bright mt-1 shrink-0" />
                      <span className="text-white/85">{pick(c.address)}</span>
                    </li>
                  ) : null}
                  {c.phone ? (
                    <li className="flex items-center gap-3">
                      <Phone size={18} strokeWidth={1.5} className="text-brand-primary-bright shrink-0" />
                      <a href={`tel:${c.phone}`} className="text-white hover:text-brand-primary-bright font-medium" data-testid="company-phone">{c.phone}</a>
                    </li>
                  ) : null}
                  {c.email ? (
                    <li className="flex items-center gap-3">
                      <Mail size={18} strokeWidth={1.5} className="text-brand-primary-bright shrink-0" />
                      <a href={`mailto:${c.email}`} className="text-white hover:text-brand-primary-bright" data-testid="company-email">{c.email}</a>
                    </li>
                  ) : null}
                  {pick(c.working_hours) ? (
                    <li className="flex items-start gap-3">
                      <Clock size={18} strokeWidth={1.5} className="text-brand-primary-bright mt-1 shrink-0" />
                      <span className="text-white/85">{pick(c.working_hours)}</span>
                    </li>
                  ) : null}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              {hasMap ? (
                <div className="w-full aspect-[4/3] bg-white/5 border border-white/10 overflow-hidden" data-testid="company-map">
                  <iframe
                    title={`Map ${pick(c.name)}`}
                    className="w-full h-full"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapBbox}&layer=mapnik&marker=${mapMarker}`}
                    loading="lazy"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <LeadCallout settings={settings} />
    </div>
  );
};
